/**
 * Triplanar world-space material — the castellan trick.
 * One MeshStandardMaterial patch: samples an albedo+height canvas along world
 * axes (blended by normal), multiplies albedo, and perturbates the normal from
 * the height channel's screen-space derivatives (tangent-free bump). Masonry
 * courses keep one physical scale across every piece and line up where pieces
 * meet, with no UVs authored anywhere.
 */
import * as THREE from 'three';

export interface TriplanarOptions {
  /** Meters covered by one texture repeat. */
  scale?: number;
  bumpScale?: number;
  roughness?: number;
  metalness?: number;
  color?: THREE.ColorRepresentation;
}

const VERT_HEAD = /* glsl */ `
varying vec3 vTriWorldPos;
varying vec3 vTriWorldNormal;
`;

const VERT_BODY = /* glsl */ `
vec3 triTransformed = transformed;
mat3 triModel = mat3(modelMatrix);
#ifdef USE_INSTANCING
  triModel = triModel * mat3(instanceMatrix);
#endif
vTriWorldPos = (modelMatrix * (
#ifdef USE_INSTANCING
  instanceMatrix *
#endif
  vec4(triTransformed, 1.0))).xyz;
vTriWorldNormal = normalize(triModel * objectNormal);
`;

const FRAG_HEAD = /* glsl */ `
uniform sampler2D uTriTex;
uniform float uTriScale;
uniform float uTriBump;
uniform vec3 uTriTint;
varying vec3 vTriWorldPos;
varying vec3 vTriWorldNormal;
float gTriH;

vec4 triSample(vec2 uvx, vec2 uvy, vec2 uvz, vec3 w) {
  vec4 cx = texture2D(uTriTex, uvx);
  vec4 cy = texture2D(uTriTex, uvy);
  vec4 cz = texture2D(uTriTex, uvz);
  vec3 b = pow(abs(w), vec3(9.0));
  b /= (b.x + b.y + b.z);
  return cx * b.x + cy * b.y + cz * b.z;
}

// three.js bumpmap math on our height value (no tangents needed).
vec3 triPerturb(vec3 surfPos, vec3 surfNorm, float Hll) {
  vec3 vSigmaX = dFdx(surfPos);
  vec3 vSigmaY = dFdy(surfPos);
  vec3 R1 = cross(vSigmaY, surfNorm);
  vec3 R2 = cross(surfNorm, vSigmaX);
  float fDet = dot(vSigmaX, R1);
  if (abs(fDet) < 1.0e-8) fDet = 1.0e-8;
  vec3 vGrad = sign(fDet) * (dFdx(Hll) * R1 + dFdy(Hll) * R2);
  return normalize(abs(fDet) * surfNorm - vGrad);
}
`;

const MAP_BODY = /* glsl */ `
{
  vec3 w = normalize(vTriWorldNormal);
  vec2 ux = vTriWorldPos.zy * uTriScale;
  vec2 uy = vTriWorldPos.xz * uTriScale;
  vec2 uz = vTriWorldPos.xy * uTriScale;
  vec4 t = triSample(ux, uy, uz, w);
  diffuseColor.rgb *= t.rgb * uTriTint;
  gTriH = t.a;
}
`;

const NORMAL_TAIL = /* glsl */ `
{
  // Kill bump shimmer: fade to zero at grazing angles and with distance —
  // screen-space height derivatives are only trustworthy face-on and up close.
  float facing = abs(dot(normal, normalize(vViewPosition)));
  float bumpFade = smoothstep(0.12, 0.45, facing) / (1.0 + length(vViewPosition) * 0.04);
  Hll = uTriBump * gTriH * bumpFade;
  normal = triPerturb(-vViewPosition, normal, Hll);
}
`;

export function createTriplanarMaterial(
  tex: THREE.Texture,
  opts: TriplanarOptions = {},
): THREE.MeshStandardMaterial {
  const material = new THREE.MeshStandardMaterial({
    roughness: opts.roughness ?? 0.93,
    metalness: opts.metalness ?? 0.0,
    color: opts.color ?? 0xffffff,
    dithering: true,
  });
  const uniforms = {
    uTriTex: { value: tex },
    uTriScale: { value: 1 / (opts.scale ?? 2.8) },
    uTriBump: { value: opts.bumpScale ?? 0.9 },
    uTriTint: { value: new THREE.Color(opts.color ?? 0xffffff) },
  };
  void material.color;
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTriTex = uniforms.uTriTex;
    shader.uniforms.uTriScale = uniforms.uTriScale;
    shader.uniforms.uTriBump = uniforms.uTriBump;
    shader.uniforms.uTriTint = uniforms.uTriTint;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>\n${VERT_HEAD}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>\n${VERT_BODY}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${FRAG_HEAD}`)
      .replace('#include <map_fragment>', `#include <map_fragment>\n${MAP_BODY}`)
      .replace(
        '#include <normal_fragment_begin>',
        `float Hll;\n#include <normal_fragment_begin>\n${NORMAL_TAIL}`,
      );
  };
  material.customProgramCacheKey = () => 'castle-triplanar-v1';
  return material;
}






